import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type {
  BusinessItemInput,
  ItemLog,
  ItemPriceEntry,
} from './item-master.api';

/**
 * The *business item* — what one store says about stocking one catalogue item.
 *
 * The item is the product; the business item is the offer: its price, its
 * barcode at that store, which aisle it sits in, whether the kiosk shows it,
 * and which requirements, sides and add-ons come with it.
 *
 * Everything here is checked against docs/grocefy-api.swagger.json. Three
 * things it does not say, taken from the original instead:
 *
 *   - `catalog/business/{id}/update` takes a *partial* body. The old drawer
 *     posted `{ IsVisible: false }` alone to flip one switch, and the binding
 *     model's fields are all nullable, so an omitted field is left alone.
 *   - `{businessId}/deli/{noun}` is not in the spec at all. It is what the
 *     original asks for the modifiers of one store when the client does not
 *     keep them globally.
 *   - the log endpoints answer `{ logs, count }` under `data`, which the spec
 *     types only as `object`.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

// ------------------------------------------------------ the item at a store

/**
 * Save what one store says about an item.
 *
 * The body may carry `otherBusinessItemIds`, in which case the same edit lands
 * on those stores too — one request, not one per store.
 */
export function useUpdateBusinessItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      businessItemId,
      body,
    }: {
      businessItemId: string;
      body: BusinessItemInput;
    }) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/business/${businessItemId}/update`, body)
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['item-master'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
    },
  });
}


/**
 * Stop a store stocking an item, or start again.
 *
 * There is no archive endpoint of its own: `isDeleted` is a field on the same
 * update, and the body may carry nothing else. The item itself is untouched —
 * every other store goes on selling it.
 */
export function useArchiveBusinessItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      businessItemId,
      archived,
    }: {
      businessItemId: string;
      archived: boolean;
    }) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/business/${businessItemId}/update`, {
          isDeleted: archived,
        })
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['item-master'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
    },
  });
}

/** The lists a store's own catalogue is arranged by. */
export function useMenuSections(businessId: string | null) {
  return useQuery({
    queryKey: ['business-item', 'sections', businessId],
    queryFn: () =>
      get<{ results: MenuSection[] }>(`businesses/${businessId}/menusections`),
    select: (data) =>
      [...(data.results ?? [])].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      ),
    enabled: !!businessId,
    staleTime: 10 * 60 * 1000,
  });
}

/** `MenuSectionDto`, trimmed to what a picker needs. */
export interface MenuSection {
  id: string;
  name: string | null;
  nameEng: string | null;
  superSectionName: string | null;
  superSectionNameEng: string | null;
}

/** Both answer a bare list of strings; blanks are filtered out. */
function useStoreStrings(kind: 'aisle' | 'groups', businessId: string | null) {
  return useQuery({
    queryKey: ['business-item', kind, businessId],
    queryFn: () =>
      get<{ results: (string | null)[] }>(`businesses/${businessId}/${kind}`),
    select: (data) =>
      (data.results ?? []).filter(
        (value): value is string => !!value && value.trim() !== ''
      ),
    enabled: !!businessId,
    staleTime: 10 * 60 * 1000,
  });
}

export const useAisles = (businessId: string | null) =>
  useStoreStrings('aisle', businessId);

export const useGroups = (businessId: string | null) =>
  useStoreStrings('groups', businessId);

// ---------------------------------------------------------------- barcodes

/** `BusinessItemUpcDto`. The list answers a bare array, not `{ results }`. */
export interface BusinessItemUpc {
  id: string;
  businessItemId: number;
  upc: string | null;
  isPrimary: boolean;
  createdAt: string | null;
}

export function useBusinessItemUpcs(businessItemId: string | null) {
  return useQuery({
    queryKey: ['business-item', 'upcs', businessItemId],
    queryFn: () => get<BusinessItemUpc[]>(`businesses/upcs/${businessItemId}`),
    enabled: !!businessItemId,
  });
}

function useInvalidateUpcs(businessItemId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['business-item', 'upcs', businessItemId],
    });
    void queryClient.invalidateQueries({ queryKey: ['item-master'] });
  };
}

export function useCreateBusinessItemUpc(businessItemId: string | null) {
  const invalidate = useInvalidateUpcs(businessItemId);

  return useMutation({
    mutationFn: (upc: string) =>
      unwrap<unknown>(
        api.post('businesses/upcs/create', { businessItemId, upc: upc.trim() })
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateBusinessItemUpc(businessItemId: string | null) {
  const invalidate = useInvalidateUpcs(businessItemId);

  return useMutation({
    mutationFn: ({ upcId, upc }: { upcId: string; upc: string }) =>
      unwrap<unknown>(
        api.post('businesses/upcs/update', { upcId, upc: upc.trim() })
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteBusinessItemUpc(businessItemId: string | null) {
  const invalidate = useInvalidateUpcs(businessItemId);

  return useMutation({
    mutationFn: (upcId: string) =>
      unwrap<unknown>(api.delete(`businesses/upcs/${upcId}`)),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryBusinessItemUpc(businessItemId: string | null) {
  const invalidate = useInvalidateUpcs(businessItemId);

  return useMutation({
    mutationFn: (upcId: string) =>
      unwrap<unknown>(
        api.post(`businesses/upcs/${businessItemId}/${upcId}/primary`, {})
      ),
    onSuccess: invalidate,
  });
}

/**
 * Ask the store to work out which barcode should be primary.
 *
 * It is a POST that reads: the name is the server's, not a typo here.
 */
export function useResolvePrimaryUpc(businessItemId: string | null) {
  const invalidate = useInvalidateUpcs(businessItemId);

  return useMutation({
    mutationFn: () =>
      unwrap<unknown>(
        api.post(`businesses/upcs/${businessItemId}/getprimary`, {})
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------ prices

/**
 * Create, replace or remove one of a store's prices.
 *
 * All three go through the business item's own update as a one-entry `prices`
 * array. The `catalog/price/...` endpoints the original used belong to the
 * external POS prices, and removing a price only works from in here.
 *
 * A new row carries no `id`; an existing one carries its own, which is what
 * the server matches on before falling back to name and ordering.
 */
export function useSaveItemPrice(businessItemId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entry: ItemPriceEntry) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/business/${businessItemId}/update`, {
          prices: [entry],
        })
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['item-master'] }),
  });
}

// -------------------------------------------------------------------- logs

/**
 * A business item log entry.
 *
 * Wider than the catalogue item's `ItemLog`: these carry `location` (the
 * operation that wrote the entry, e.g. `ApplyPrice`) and `data`, the metadata
 * dictionary as JSON. `data` is what the drawer unpacks into readable fields —
 * see `log-payload.ts`.
 */
export interface BusinessItemLog extends ItemLog {
  location?: string | null;
  data?: string | null;
}

export function useBusinessItemLogs(
  businessItemId: string | null,
  page: number,
  size: number
) {
  return useQuery({
    queryKey: ['business-item', 'logs', businessItemId, page, size],
    queryFn: () =>
      get<{ logs: BusinessItemLog[]; count: number }>(
        `admin/businessitemlogs/${businessItemId}?page=${page}&pageSize=${size}`
      ),
    enabled: !!businessItemId,
  });
}

// --------------------------------------------- requirements, sides, add-ons

export type ModifierKind = 'requirement' | 'side' | 'addon';

/**
 * The three modifiers differ only by a noun, and the noun is spelt differently
 * in each of the three places it appears — plural to list, short to assign,
 * singular to remove.
 */
const MODIFIER_NOUNS = {
  requirement: { list: 'requirements', assign: 'assignreq', remove: 'requirement' },
  side: { list: 'sides', assign: 'assignside', remove: 'side' },
  addon: { list: 'addons', assign: 'assignaddon', remove: 'addon' },
} as const;

/** `RequirementCategoryDto` / `SideCategoryDto` / `AddOnCategoryDto`. */
export interface ModifierCategory {
  id: string;
  name: string | null;
  internalName: string | null;
  minRequired: number;
  maxRequired: number;
  items: { id: string; name?: string | null }[] | null;
}

/**
 * Which modifiers this item already carries.
 *
 * Sent as a POST with both ids because the server answers for whichever one is
 * filled: the business item when the store keeps its own, the item when the
 * client keeps one set for everybody.
 */
export function useAssignedModifiers(
  kind: ModifierKind,
  {
    itemId,
    businessItemId,
    enabled,
  }: { itemId: string | null; businessItemId: string | null; enabled: boolean }
) {
  return useQuery({
    queryKey: ['business-item', kind, 'assigned', itemId, businessItemId],
    queryFn: () =>
      unwrap<{ results: ModifierCategory[] }>(
        api.post(`deli/${MODIFIER_NOUNS[kind].list}`, { itemId, businessItemId })
      ),
    select: (data) => data.results ?? [],
    enabled,
  });
}

/**
 * Every modifier that could be assigned.
 *
 * `{businessId}/deli/{noun}` is the store's own set and is not in the swagger;
 * the bare `deli/{noun}` is the client-wide set. Which one to ask for is the
 * same question as which one to assign against.
 */
export function useAllModifiers(
  kind: ModifierKind,
  { businessId, enabled }: { businessId: string | null; enabled: boolean }
) {
  const path = businessId
    ? `${businessId}/deli/${MODIFIER_NOUNS[kind].list}`
    : `deli/${MODIFIER_NOUNS[kind].list}`;

  return useQuery({
    queryKey: ['business-item', kind, 'all', businessId],
    queryFn: () => get<{ results: ModifierCategory[] }>(path),
    select: (data) => data.results ?? [],
    enabled,
  });
}

/** Where an assignment lives: at one store, or across the whole client. */
export interface ModifierScope {
  /** The business item, when the store keeps its own modifiers. */
  businessItemId: string | null;
  /** The catalogue item, when one set is kept for every store. */
  itemId: string | null;
  /** Null when assignments are global. */
  businessId: string | null;
}

function useInvalidateModifiers(kind: ModifierKind) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['business-item', kind] });
}

/** Assignment is a PUT with no body; the ids in the path are the whole request. */
export function useAssignModifier(kind: ModifierKind, scope: ModifierScope) {
  const invalidate = useInvalidateModifiers(kind);
  const { assign } = MODIFIER_NOUNS[kind];

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(
        api.put(
          scope.businessItemId
            ? `deli/${scope.businessItemId}/${assign}/${id}`
            : `deli/global/${scope.itemId}/${assign}/${id}`,
          {}
        )
      ),
    onSuccess: invalidate,
  });
}

export function useUnassignModifier(kind: ModifierKind, scope: ModifierScope) {
  const invalidate = useInvalidateModifiers(kind);
  const { remove } = MODIFIER_NOUNS[kind];

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(
        api.post(
          scope.businessItemId
            ? `deli/delete/${remove}/${scope.businessItemId}/${id}`
            : `deli/global/delete/${remove}/${scope.itemId}/${id}`,
          {}
        )
      ),
    onSuccess: invalidate,
  });
}

// -------------------------------------------------------- copy a catalogue

/**
 * Copy one store's whole catalogue onto another.
 *
 * The body is a bare array of destination ids — the original sent exactly one,
 * but the endpoint takes a list, so this does too.
 */
export function useCopyCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fromId, toIds }: { fromId: string; toIds: string[] }) =>
      unwrap<unknown>(api.post(`businesses/catalog/copyto/${fromId}`, toIds)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['item-master'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
    },
  });
}
